import { createResource, type Accessor } from "solid-js";
import { fetchComponentSettings, fetchDynamicType } from "../../lib/api";
import { useAuth } from "../../lib/auth-store";
import {
  DIMENSION_TYPE_OVERVIEW_COMPONENT_KEY,
  overviewNavigationLabelVersion,
  readOverviewNavigationLabel,
} from "../../lib/overview-navigation-label-cache";
import type { DimensionTypeOverviewConfig } from "../../lib/types";
import type { DimensionTypeName } from "../../lib/hooks/use-dimension-type-name";

// Reads the overview's configuration (not its resolution) for the dimension
// type it names, then that type's display name. Null when the overview names
// no type or the type does not exist; a failed read rejects.
async function readLabel(): Promise<string | null> {
  const settings = await fetchComponentSettings<DimensionTypeOverviewConfig>(DIMENSION_TYPE_OVERVIEW_COMPONENT_KEY);
  const dimensionTypeId = settings.config.dimensionTypeId?.trim();
  if (!dimensionTypeId) {
    return null;
  }
  const dimensionType = await fetchDynamicType(dimensionTypeId);
  if (!dimensionType) {
    return null;
  }
  return dimensionType.displayName ?? dimensionType.name;
}

/**
 * The Overview navigation entry's label: the display name of the dimension
 * type the overview is configured for. It is read once per session for the
 * signed-in organisation and user, and again only when the organisation or
 * user changes or the label is invalidated (a dimension type renamed or
 * deleted, or the overview's configuration saved or reset, through the app).
 * A failed read is reported through `failed` and read again by `retry`.
 */
export function useOverviewNavigationLabel(enabled: Accessor<boolean>): DimensionTypeName {
  const auth = useAuth();
  const source = () => {
    if (!enabled()) {
      return undefined;
    }
    const organisationId = auth.effectiveOrganisationId();
    const user = auth.user();
    if (!organisationId || !user) {
      return undefined;
    }
    const userId = user.assumedUserId ?? user.userId;
    return { key: `${organisationId}|${userId}`, version: overviewNavigationLabelVersion() };
  };

  const [label, { refetch }] = createResource(source, ({ key }) => readOverviewNavigationLabel(key, readLabel));

  // Reading an errored resource throws, so the failure is checked first.
  const failed = () => label.state === "errored";
  return {
    name: () => (failed() ? null : (label() ?? null)),
    loading: () => label.loading,
    failed,
    retry: () => {
      void refetch();
    },
  };
}
